import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { ApiServices } from "../../../utils/ApiService";

const fieldStyle = { marginBottom: "20px" };

export default function FileFormatAddEditPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();

  const [saving, setSaving] = useState(false);

  const [docCategories, setDocCategories] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [possessions, setPossessions] = useState([]);

  const [formData, setFormData] = useState({
    doc_category_id: "",
    doc_type_id: "",
    doc_possession_id: "",
    from_or_thereafter: "",
    format: "",
  });

  const [errors, setErrors] = useState({});

  const fromOptions = [
    { value: "None", label: "None" },
    { value: "From", label: "From" },
    { value: "Thereafter", label: "Thereafter" },
  ];

  // ----------------------------------
  // Load master data + edit data
  // ----------------------------------
  useEffect(() => {
    const init = async () => {
      try {
        // Load categories and possessions
        const [catRes, posRes] = await Promise.all([
          ApiServices.get("file-format/categories"),
          ApiServices.get("file-format/possessions"),
        ]);
        setDocCategories(catRes?.data?.docCategories || []);
        setPossessions(posRes?.data?.possessions || []);

        // Edit mode: load existing file format
        if (id) {
          const res = await ApiServices.get(`file-format/${id}`);
          if (res?.data?.fileFormat) {
            const ff = res.data.fileFormat;

            // Load types for the category
            const typeRes = await ApiServices.get(`file-format/types/${ff.doc_category_id}`);
            setDocTypes(typeRes?.data?.docTypes || []);

            // Set form data
            setFormData({
              doc_category_id: ff.doc_category_id,
              doc_type_id: ff.doc_type_id,
              doc_possession_id: ff.doc_possession_id || "",
              from_or_thereafter: ff.from_or_thereafter,
              format: ff.format,
            });
          }
        }
      } catch (err) {
        console.error(err);
        enqueueSnackbar("Error loading data", { variant: "error" });
      }
    };

    init();
  }, [id, enqueueSnackbar]);

  // ----------------------------------
  // Load types when category changes
  // ----------------------------------
  const loadTypesByCategory = async (categoryId) => {
    try {
      const res = await ApiServices.get(`file-format/types/${categoryId}`);
      setDocTypes(res?.data?.docTypes || []);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error loading document types", { variant: "error" });
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "doc_category_id") {
      setFormData((prev) => ({ ...prev, doc_type_id: "" }));
      await loadTypesByCategory(value);
    }
  };

  // ----------------------------------
  // Validation
  // ----------------------------------
  const validate = () => {
    const newErrors = {};
    if (!formData.doc_category_id) newErrors.doc_category_id = "Category is required";
    if (!formData.doc_type_id) newErrors.doc_type_id = "Type is required";
    if (!formData.from_or_thereafter) newErrors.from_or_thereafter = "From/Thereafter is required";
    if (!formData.format.trim()) newErrors.format = "Format is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ----------------------------------
  // Save
  // ----------------------------------
  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSaving(true);
      const apiUrl = id ? `file-format/file-format-update/${id}` : "file-format/file-format-add";
      const apiMethod = id ? ApiServices.put : ApiServices.post;
      await apiMethod(apiUrl, formData);

      enqueueSnackbar(`File format ${id ? "updated" : "added"} successfully`, {
        variant: "success"
      });
      navigate("/settings/file-format-master");
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error saving file format", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => navigate("/settings/file-format-master");

  // ----------------------------------
  // Render
  // ----------------------------------
  return (
    <Box sx={{ width: "100%", bgcolor: "#f0f2f5", py: 5 }}>
      <Paper
        elevation={4}
        sx={{
          maxWidth: 700,
          mx: "auto",
          p: 4,
          borderRadius: 3,
          background: "white",
        }}
      >
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {id ? "Edit File Format" : "Add New File Format"}
        </Typography>

        {/* Category */}
        <div style={fieldStyle}>
          <FormControl fullWidth size="medium" error={!!errors.doc_category_id}>
            <InputLabel>Document Category *</InputLabel>
            <Select
              name="doc_category_id"
              label="Document Category *"
              value={formData.doc_category_id}
              onChange={handleChange}
            >
              {docCategories.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.doc_category_name}
                </MenuItem>
              ))}
            </Select>
            {errors.doc_category_id && <FormHelperText>{errors.doc_category_id}</FormHelperText>}
          </FormControl>
        </div>

        {/* Type */}
        <div style={fieldStyle}>
          <FormControl fullWidth size="medium" error={!!errors.doc_type_id}>
            <InputLabel>Document Type *</InputLabel>
            <Select
              name="doc_type_id"
              label="Document Type *"
              value={formData.doc_type_id}
              onChange={handleChange}
            >
              {docTypes.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.doc_type}
                </MenuItem>
              ))}
            </Select>
            {errors.doc_type_id && <FormHelperText>{errors.doc_type_id}</FormHelperText>}
          </FormControl>
        </div>

        {/* Possession */}
        <div style={fieldStyle}>
          <FormControl fullWidth size="medium">
            <InputLabel>Possession</InputLabel>
            <Select
              name="doc_possession_id"
              label="Possession"
              value={formData.doc_possession_id || ""}
              onChange={handleChange}
            >
              {possessions.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.possession}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* From / Thereafter */}
        <div style={fieldStyle}>
          <FormControl fullWidth size="medium" error={!!errors.from_or_thereafter}>
            <InputLabel>From / Thereafter *</InputLabel>
            <Select
              name="from_or_thereafter"
              label="From / Thereafter *"
              value={formData.from_or_thereafter}
              onChange={handleChange}
            >
              {fromOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
            {errors.from_or_thereafter && (
              <FormHelperText>{errors.from_or_thereafter}</FormHelperText>
            )}
          </FormControl>
        </div>

        {/* Format */}
        <div style={fieldStyle}>
          <TextField
            fullWidth
            name="format"
            label="Format *"
            value={formData.format}
            onChange={handleChange}
            multiline
            rows={8}
            error={!!errors.format}
            helperText={errors.format}
          />
        </div>

        <Box mt={3} display="flex" justifyContent="center" gap={2}>
          <Button
            variant="contained"
            size="large"
            onClick={handleCancel}
            sx={{ px: 5, py: 1.5, borderRadius: 2 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            size="large"
            onClick={handleSave}
            disabled={saving}
            sx={{
              px: 5,
              py: 1.5,
              fontWeight: "bold",
              borderRadius: 2,
              background: "#1976d2",
              "&:hover": { background: "#1565c0" },
            }}
          >
            {saving ? <CircularProgress size={24} sx={{ color: "white" }} /> : id ? "Update" : "Save"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
